(function () {
  'use strict';

  const REJECTION_MESSAGE = 'Your artwork requires more than one HTV colour. We can only produce a one-colour HTV iron-on. Please upload a one-colour version.';
  const TEXT_LIMIT_MESSAGE = 'This wording cannot be produced clearly at the selected width. Choose a larger HTV iron-on, shorten the wording, or use a narrower font.';

  function money(cents) {
    return `€${(cents / 100).toFixed(2)}`;
  }

  class UCDHTVCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.core = window.UCDHTVCustomizerCore;
      this.form = this.querySelector('form');
      this.modeInputs = [...this.querySelectorAll('input[name^="ucdhtv-mode-"]')];
      this.wording = this.querySelector('[data-ucdhtv-wording]');
      this.font = this.querySelector('[data-ucdhtv-font]');
      this.spacing = this.querySelector('[data-ucdhtv-spacing]');
      this.size = this.querySelector('[data-ucdhtv-size]');
      this.quantity = this.querySelector('[data-ucdhtv-quantity]');
      this.file = this.querySelector('[data-ucdhtv-file]');
      this.sketchFile = this.querySelector('[data-ucdhtv-sketch-file]');
      this.fileStatus = this.querySelector('[data-ucdhtv-file-status]');
      this.layout = this.querySelector('[data-ucdhtv-layout]');
      this.stage = this.querySelector('[data-ucdhtv-stage]');
      this.design = this.querySelector('[data-ucdhtv-design]');
      this.previewText = this.querySelector('[data-ucdhtv-preview-text]');
      this.artworkCanvas = this.querySelector('[data-ucdhtv-artwork]');
      this.error = this.querySelector('[data-ucdhtv-error]');
      this.submit = this.querySelector('[data-ucdhtv-submit]');
      this.setupWarning = this.querySelector('[data-ucdhtv-setup-warning]');
      this.discountCopy = this.querySelector('[data-ucdhtv-discount-copy]');
      this.materialButtons = [...this.querySelectorAll('[data-ucdhtv-material]')];
      this.material = this.materialButtons[0];
      this.artworkReady = false;
      this.artworkError = '';
      this.sourceImage = null;
      this.objectUrl = '';
      this.submitting = false;
      this.variants = this.readVariants();
      this.renderDiscountCopy();
      this.bindEvents();
      this.update();
    }

    readVariants() {
      try {
        return JSON.parse(this.querySelector('[data-ucdhtv-variants]').textContent);
      } catch (error) {
        return [];
      }
    }

    renderDiscountCopy() {
      if (!this.discountCopy) return;
      const tiers = this.core.DISCOUNT_TIERS
        .map((tier) => `${tier.minimumQuantity}+ = ${tier.percent}%`)
        .join(', ');
      this.discountCopy.textContent = `HTV quantity savings: ${tiers}. Discounts are applied automatically at checkout.`;
    }

    bindEvents() {
      this.modeInputs.forEach((input) => input.addEventListener('change', () => this.update()));
      [this.wording, this.font, this.spacing, this.size, this.quantity, this.layout].forEach((control) => {
        control.addEventListener('input', () => this.update());
        control.addEventListener('change', () => this.update());
      });
      this.materialButtons.forEach((button) => button.addEventListener('click', () => {
        this.materialButtons.forEach((item) => item.classList.remove('is-selected'));
        button.classList.add('is-selected');
        this.material = button;
        this.renderArtwork();
        this.update();
      }));
      this.file.addEventListener('change', () => this.handleFile());
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
      window.addEventListener('resize', () => this.fitPreviewText());
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fitPreviewText());
    }

    get mode() {
      return this.modeInputs.find((input) => input.checked).value;
    }

    get textLines() {
      return this.wording.value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    }

    property(name, value, enabled = true) {
      const input = this.querySelector(`[data-ucdhtv-property="${name}"]`);
      if (input) {
        input.value = value;
        input.disabled = !enabled;
      }
    }

    update() {
      const mode = this.mode;
      const hasText = mode !== 'artwork';
      const hasArtwork = mode !== 'text';
      const size = Number(this.size.value);
      const lines = hasText ? Math.max(1, this.textLines.length) : 1;
      const spacing = lines === 1 ? 0.9 : Number(this.spacing.value);
      const quantity = Math.max(1, Number(this.quantity.value) || 1);

      this.querySelector('[data-ucdhtv-text-controls]').hidden = !hasText;
      this.querySelector('[data-ucdhtv-upload-controls]').hidden = !hasArtwork;
      this.querySelector('[data-ucdhtv-both-layout]').hidden = mode !== 'both';
      this.querySelector('[data-ucdhtv-artwork-confirm]').hidden = !hasArtwork;
      this.querySelector('[data-ucdhtv-one-colour-confirm]').hidden = !hasArtwork;
      this.wording.disabled = !hasText;
      this.wording.required = hasText;
      this.file.disabled = !hasArtwork;
      this.file.required = hasArtwork;
      this.querySelector('[data-ucdhtv-artwork-confirm] input').required = hasArtwork;
      this.querySelector('[data-ucdhtv-artwork-confirm] input').disabled = !hasArtwork;
      this.querySelector('[data-ucdhtv-one-colour-confirm] input').required = hasArtwork;
      this.querySelector('[data-ucdhtv-one-colour-confirm] input').disabled = !hasArtwork;
      this.spacing.disabled = !hasText || lines === 1;
      this.previewText.hidden = !hasText;
      this.artworkCanvas.hidden = !hasArtwork || !this.artworkReady;

      const whitePreview = this.material.dataset.ucdhtvId === 'white';
      this.stage.classList.toggle('is-dark', whitePreview);
      this.querySelector('[data-ucdhtv-background-note]').hidden = !whitePreview;
      const backgroundConfirm = this.querySelector('[data-ucdhtv-background-confirm]');
      backgroundConfirm.hidden = !whitePreview;
      backgroundConfirm.querySelector('input').required = whitePreview;
      backgroundConfirm.querySelector('input').disabled = !whitePreview;

      this.design.classList.toggle('ucdhtv__design--left', mode === 'both' && this.layout.value === 'Artwork left of text');
      this.design.classList.toggle('ucdhtv__design--above', !(mode === 'both' && this.layout.value === 'Artwork left of text'));

      const fontOption = this.font.selectedOptions[0];
      this.previewText.style.fontFamily = `'${fontOption.dataset.ucdhtvFamily}', sans-serif`;
      this.previewText.style.lineHeight = String(spacing);
      this.renderText(hasText);
      this.applyMaterialToText();

      const maxLines = this.core.maxLinesForSize(size);
      this.querySelector('[data-ucdhtv-line-help]').textContent = `Up to ${maxLines} lines at this size. Press Enter to start a new line.`;
      this.querySelector('[data-ucdhtv-size-label]').textContent = `${size}" wide`;

      let validationMessage = '';
      if (hasText && this.textLines.length > maxLines) {
        validationMessage = `${size}" HTV iron-ons can have no more than ${maxLines} text lines. Choose 6" or larger for 3 lines.`;
      } else if (hasText && this.textLines.length && !this.core.textFits(this.textLines, size, Number(fontOption.dataset.ucdhtvWidth))) {
        validationMessage = TEXT_LIMIT_MESSAGE;
      } else if (hasArtwork && this.artworkError) {
        validationMessage = this.artworkError;
      } else if (hasArtwork && this.file.files.length && !this.artworkReady) {
        validationMessage = 'Your artwork is still being checked. Please wait a moment.';
      }

      let calculatedUnitCents;
      try {
        calculatedUnitCents = this.core.calculateUnitPriceCents({ size, lines, spacing });
      } catch (error) {
        validationMessage = validationMessage || error.message;
        calculatedUnitCents = this.core.BASE_PRICE_CENTS[size];
      }
      const variant = this.core.findVariant(this.variants, size, lines, spacing);
      const unitCents = this.core.resolveVariantPriceCents(variant, calculatedUnitCents);
      const total = this.core.calculateTotal({ unitCents, quantity });
      const configured = Boolean(variant && variant.available);
      this.querySelector('[data-ucdhtv-variant-id]').value = variant ? variant.id : '';
      this.setupWarning.hidden = configured;

      const designType = mode === 'text' ? 'Custom Text' : mode === 'artwork' ? 'Upload Artwork' : 'Text + Artwork';
      const layout = mode === 'text' ? 'Text only' : mode === 'artwork' ? 'Artwork only' : this.layout.value;
      this.property('design-type', designType);
      this.property('font', this.font.value, hasText);
      this.property('material', this.material.dataset.ucdhtvLabel);
      this.property('spacing', `${spacing.toFixed(1)}×`, hasText && lines > 1);
      this.property('layout', layout, mode === 'both');
      const approval = hasArtwork
        ? 'Design confirmed; artwork rights and one-colour policy confirmed'
        : 'Spelling, layout, dimensions and colour confirmed';
      this.property('approval', whitePreview ? `${approval}; preview background understood` : approval);

      this.querySelector('[data-ucdhtv-summary-size]').textContent = `${size}" wide`;
      this.querySelector('[data-ucdhtv-summary-material]').textContent = this.material.dataset.ucdhtvLabel;
      this.querySelector('[data-ucdhtv-summary-unit]').textContent = money(unitCents);
      this.querySelector('[data-ucdhtv-summary-discount]').textContent = total.discountPercent ? `${total.discountPercent}%` : 'None';
      this.querySelector('[data-ucdhtv-summary-total]').textContent = money(total.totalCents);
      this.setError(validationMessage);
      this.submit.disabled = Boolean(validationMessage) || !configured || (hasText && !this.textLines.length) || (hasArtwork && !this.artworkReady);
      this.fitPreviewText();
    }

    renderText(hasText) {
      this.previewText.replaceChildren();
      const lines = this.textLines.length ? this.textLines : ['YOUR TEXT'];
      lines.forEach((line) => {
        const span = document.createElement('span');
        span.textContent = line;
        this.previewText.appendChild(span);
      });
      this.previewText.hidden = !hasText;
    }

    fitPreviewText() {
      if (this.previewText.hidden) return;
      const maximum = this.design.classList.contains('ucdhtv__design--left') ? 48 : 72;
      const available = this.design.classList.contains('ucdhtv__design--left') ? this.design.clientWidth * 0.5 : this.design.clientWidth * 0.92;
      let fontSize = maximum;
      this.previewText.style.fontSize = `${fontSize}px`;
      while (fontSize > 20 && [...this.previewText.children].some((line) => line.scrollWidth > available)) {
        fontSize -= 2;
        this.previewText.style.fontSize = `${fontSize}px`;
      }
    }

    applyMaterialToText() {
      this.previewText.style.background = 'none';
      this.previewText.style.backgroundClip = '';
      this.previewText.style.webkitBackgroundClip = '';
      this.previewText.style.color = this.material.dataset.ucdhtvColour;
    }

    async handleFile() {
      this.artworkReady = false;
      this.artworkError = '';
      this.sourceImage = null;
      const file = this.file.files[0];
      if (!file) {
        this.fileStatus.textContent = 'No file selected.';
        this.fileStatus.className = 'ucdhtv__file-status';
        this.update();
        return;
      }
      const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
      const extensionAllowed = /\.(png|jpe?g|svg)$/i.test(file.name);
      if ((!allowed.includes(file.type) && !extensionAllowed) || file.size > 10 * 1024 * 1024) {
        this.rejectArtwork(file.size > 10 * 1024 * 1024 ? 'The artwork file is larger than 10 MB. Please upload a smaller PNG, JPG or SVG.' : 'Please upload a PNG, JPG or SVG file.');
        return;
      }
      this.fileStatus.textContent = 'Checking artwork colours…';
      this.fileStatus.className = 'ucdhtv__file-status';
      try {
        if (file.type === 'image/svg+xml') {
          const svg = await file.text();
          if (this.core.countSvgColours(svg) > 1) throw new Error(REJECTION_MESSAGE);
        }
        if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = URL.createObjectURL(file);
        this.sourceImage = await this.loadImage(this.objectUrl);
        const colours = this.inspectRaster(this.sourceImage);
        if (colours === 0) throw new Error('No usable artwork was found in this file. Please upload a clear one-colour design.');
        if (colours > 1) throw new Error(REJECTION_MESSAGE);
        this.artworkReady = true;
        this.fileStatus.textContent = `${file.name} — accepted as one-colour artwork.`;
        this.fileStatus.className = 'ucdhtv__file-status is-good';
        this.renderArtwork();
      } catch (error) {
        this.rejectArtwork(error.message || 'This artwork could not be read. Please upload a different PNG, JPG or SVG.');
        return;
      }
      this.update();
    }

    rejectArtwork(message) {
      this.artworkReady = false;
      this.artworkError = message;
      this.artworkCanvas.hidden = true;
      this.fileStatus.textContent = message;
      this.fileStatus.className = 'ucdhtv__file-status is-bad';
      this.update();
    }

    loadImage(url) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('This artwork could not be read. Please upload a different PNG, JPG or SVG.'));
        image.src = url;
      });
    }

    drawContained(context, image, width, height) {
      const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
      const drawWidth = image.naturalWidth * scale;
      const drawHeight = image.naturalHeight * scale;
      context.clearRect(0, 0, width, height);
      context.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
    }

    inspectRaster(image) {
      const canvas = document.createElement('canvas');
      canvas.width = 240;
      canvas.height = 180;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      this.drawContained(context, image, canvas.width, canvas.height);
      return this.core.countRasterColours(context.getImageData(0, 0, canvas.width, canvas.height).data);
    }

    renderArtwork() {
      if (!this.sourceImage || !this.artworkReady) return;
      const canvas = this.artworkCanvas;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      this.drawContained(context, this.sourceImage, canvas.width, canvas.height);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      const colour = this.hexToRgb(this.material.dataset.ucdhtvColour);
      let hasNonWhite = false;
      for (let i = 0; i < pixels.length; i += 4) {
        if (pixels[i + 3] >= 64 && !(pixels[i] > 245 && pixels[i + 1] > 245 && pixels[i + 2] > 245)) hasNonWhite = true;
      }
      for (let i = 0; i < pixels.length; i += 4) {
        const nearWhite = pixels[i] > 245 && pixels[i + 1] > 245 && pixels[i + 2] > 245;
        if (pixels[i + 3] < 64 || (hasNonWhite && nearWhite)) {
          pixels[i + 3] = 0;
        } else {
          pixels[i] = colour[0]; pixels[i + 1] = colour[1]; pixels[i + 2] = colour[2];
        }
      }
      context.putImageData(imageData, 0, 0);
      canvas.hidden = false;
    }

    hexToRgb(hex) {
      const value = String(hex).replace('#', '');
      return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)];
    }

    setError(message) {
      this.error.hidden = !message;
      this.error.textContent = message;
    }

    drawCanvasContained(context, source, x, y, width, height) {
      const scale = Math.min(width / source.width, height / source.height);
      const drawWidth = source.width * scale;
      const drawHeight = source.height * scale;
      context.drawImage(source, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
    }

    drawSketchText(context, lines, x, y, width, height, family, colour, spacing) {
      const safeLines = lines.length ? lines : ['YOUR TEXT'];
      let fontSize = 150;
      const maxHeight = () => fontSize * spacing * safeLines.length;
      const fitsWidth = () => safeLines.every((line) => context.measureText(line).width <= width);
      while (fontSize > 28) {
        context.font = `800 ${fontSize}px '${family}', sans-serif`;
        if (fitsWidth() && maxHeight() <= height) break;
        fontSize -= 4;
      }
      context.fillStyle = colour;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      const lineHeight = fontSize * spacing;
      const firstY = y + height / 2 - ((safeLines.length - 1) * lineHeight) / 2;
      safeLines.forEach((line, index) => context.fillText(line, x + width / 2, firstY + index * lineHeight));
    }

    buildProductionSketch() {
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 900;
      const context = canvas.getContext('2d');
      const size = Number(this.size.value);
      const mode = this.mode;
      const whitePreview = this.material.dataset.ucdhtvId === 'white';
      const panel = { x: 60, y: 145, width: 1080, height: 600 };
      const colour = this.material.dataset.ucdhtvColour;
      const family = this.font.selectedOptions[0].dataset.ucdhtvFamily;
      const lines = this.textLines;
      const spacing = lines.length > 1 ? Number(this.spacing.value) : 0.9;

      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = '#f6c817';
      context.fillRect(60, 40, 300, 55);
      context.fillStyle = '#11141a';
      context.font = '800 29px Arial, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText('URBAN CITY DECAL', 210, 68);
      context.textAlign = 'left';
      context.font = '800 38px Arial, sans-serif';
      context.fillText('HTV IRON-ON PRODUCTION SKETCH', 390, 68);

      context.fillStyle = whitePreview ? '#171a20' : '#f0f1f4';
      context.fillRect(panel.x, panel.y, panel.width, panel.height);
      context.strokeStyle = '#c9cbd1';
      context.lineWidth = 2;
      context.strokeRect(panel.x, panel.y, panel.width, panel.height);

      if (mode === 'text') {
        this.drawSketchText(context, lines, 130, 250, 940, 390, family, colour, spacing);
      } else if (mode === 'artwork') {
        this.drawCanvasContained(context, this.artworkCanvas, 170, 205, 860, 480);
      } else if (this.layout.value === 'Artwork left of text') {
        this.drawCanvasContained(context, this.artworkCanvas, 95, 220, 470, 450);
        this.drawSketchText(context, lines, 585, 230, 510, 430, family, colour, spacing);
      } else {
        this.drawCanvasContained(context, this.artworkCanvas, 180, 175, 840, 315);
        this.drawSketchText(context, lines, 135, 500, 930, 190, family, colour, spacing);
      }

      const layout = mode === 'text' ? 'TEXT ONLY' : mode === 'artwork' ? 'ARTWORK ONLY' : this.layout.value.toUpperCase();
      context.fillStyle = '#11141a';
      context.font = '800 25px Arial, sans-serif';
      context.textAlign = 'center';
      context.fillText(`${size} INCHES WIDE  •  ${this.material.dataset.ucdhtvLabel.toUpperCase()}  •  ${layout}`, 600, 795);
      context.font = '800 25px Arial, sans-serif';
      context.fillStyle = '#b00020';
      context.fillText('MIRROR BEFORE CUTTING HTV', 600, 840);
      context.font = '600 16px Arial, sans-serif';
      context.fillStyle = '#555a64';
      context.fillText('Customer preview and this sketch are intentionally shown readable / unmirrored.', 600, 870);
      return canvas;
    }

    attachProductionSketch() {
      const canvas = this.buildProductionSketch();
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Production sketch could not be generated.'));
            return;
          }
          try {
            const file = new File([blob], 'urban-city-htv-production-sketch.png', { type: 'image/png' });
            const transfer = new DataTransfer();
            transfer.items.add(file);
            this.sketchFile.files = transfer.files;
            resolve();
          } catch (error) {
            reject(error);
          }
        }, 'image/png');
      });
    }

    async handleSubmit(event) {
      if (this.submitting) return;
      event.preventDefault();
      this.update();
      if (this.submit.disabled || !this.form.checkValidity()) {
        this.form.reportValidity();
        if (!this.error.hidden) this.error.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const label = this.submit.querySelector('span');
      const originalLabel = label.textContent;
      this.submit.disabled = true;
      label.textContent = 'Preparing production sketch…';
      try {
        await this.attachProductionSketch();
        this.submitting = true;
        HTMLFormElement.prototype.submit.call(this.form);
      } catch (error) {
        this.setError('We could not prepare your production sketch. Please try again.');
        label.textContent = originalLabel;
        this.submit.disabled = false;
        this.error.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  if (!customElements.get('ucd-htv-customizer')) customElements.define('ucd-htv-customizer', UCDHTVCustomizer);
})();
